import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import { useWorkListActions } from '../../shared/hooks/useApi/useWorkList';
import { UpdateWorkBulkInfoItemReq } from '../../shared/dto/ReqDtoRepository';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { AlertPosition, AlertType } from '../../shared/enum/EnumRepository';
import { getHintMessage } from '../../shared/constants/hintMessages';
import { useWorkViewStore } from '../../shared/store/WorkViewStore';
import { useWorkPropertyVisible } from '../../shared/hooks/useApi/useWorkPropertyVisible';
import { useValidation } from '../../shared/hooks/useValidation';
import { WorkData, WorkPropertyVisibleToggleField, hasSizeDataForUnit, hasValidMainSizeUnitForUpdate, isSameSizeData, mergeWorkSizeUpdates, normalizeWorkSizesForUpdate, SizeData } from '../../shared/dto/EntityRepository';
import { DimensionsType, SelectType, SizeUnit, WorkContentMode, WorkType, usesWorkDuration } from '../../shared/enum/EnumRepository';
import { WorkViewerInfo } from '../../shared/component/headless/input/InputBody';
import { WorkViewerInfoTextArea } from '../../shared/component/headless/textarea/TextAreaBody';
import MoleculeInputDiv from '../../shared/component/molecule/MoleculeInputDiv';
import MoleculeTextAreaDiv from '../../shared/component/molecule/MoleculeTextAreaDiv';
import OrgSelectDivVisi from '../../shared/component/organism/OrgSelectDivVisi';
import HintWrapper from '../../shared/component/headless/button/HintWrapper';
import SizeInputWithMainUnit from '../work/SizeInputWithMainUnit';
import confirmIcon from '../../asset/icon/confirm2.png';
import confirmIcon2 from '../../asset/icon/confirm3.png';
import deleteOpenIcon from '../../asset/icon/delete_open.png';
import deleteCloseIcon from '../../asset/icon/delete_close.png';
import {
  INPUT_YEAR_MAX_LENGTH,
  INPUT_YEAR_MIN_LENGTH,
  INPUT_RUNNING_TIME_MIN_LENGTH,
  INPUT_RUNNING_TIME_MAX_LENGTH,
  INPUT_SIZE_TEXT_MIN_LENGTH,
  INPUT_SIZE_TEXT_MAX_LENGTH,
} from '../../shared/constants/limits';
import { useWorkTagStore } from '../../shared/store/workTagStore';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { useWorkContentModeStore } from '../../shared/store/workContentModeStore';
import DimensionsTypeSelect from '../work/DimensionsTypeSelect';
import MoleculeDivToggle from '../../shared/component/molecule/MoleculeDivToggle';
import { usePageOwnerIdentityStore } from '../../shared/store/pageOwnerIdentityStore';
import { canShowWorkTagList } from '../../shared/util/visibility/workTagVisibility';


const MultiWorkViewer: React.FC = () => {
  const { isOwner } = useAuth();
  const { isEditMode } = useEditMode();
  const { deleteWorksBulk, updateWorkBulkInfo } = useWorkListActions();
  const { enqueueAlert } = useStandardAlertStore();
  const multiSelectedWork = useWorkViewStore((s) => s.multiSelectedWork);
  const multiHasChanged = useWorkViewStore((s) => s.multiHasChanged);
  const setMultiHasChanged = useWorkViewStore((s) => s.setMultiHasChanged);
  const { workPropertyVisible, updateWorkPropertyVisible } = useWorkPropertyVisible();
  const { checkType } = useValidation();
  const tags = useWorkTagStore((s) => s.tags);
  const listOwnerAui = useWorkTagStore((s) => s.listOwnerAui);
  const tagStatus = useWorkTagStore((s) => s.status);
  const pageOwner = usePageOwnerIdentityStore((s) => s.pageOwner);
  const canShowTags = canShowWorkTagList({
    pageOwnerAui: pageOwner?.aui,
    listOwnerAui,
    status: tagStatus,
  });
  const visibleTags = canShowTags ? tags : [];
  const workContentMode = useWorkContentModeStore((s) => s.workContentMode);
  const isWorkEng = workContentMode === WorkContentMode.ENG;

  const [year, setYear] = useState<string | null>(null);
  const [material, setMaterial] = useState<string | null>(null);
  const [materialEn, setMaterialEn] = useState<string | null>(null);
  const [workType, setWorkType] = useState<WorkType | null>(null);
  const [sizes, setSizes] = useState<SizeData[] | null>(null);
  const [sizeUpdates, setSizeUpdates] = useState<SizeData[]>([]);
  const [mainSizeUnit, setMainSizeUnit] = useState<SizeUnit | null>(null);
  const [sizeText, setSizeText] = useState<string | null>(null);
  const [dimensionsType, setDimensionsType] = useState<DimensionsType | null>(null);
  const [duration, setDuration] = useState<string | null>(null);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [isPublic, setIsPublic] = useState<boolean | null>(null);

  useEffect(() => {
    if (multiSelectedWork.length === 0) return;
    const first = multiSelectedWork[0];
    const allSame = <T,>(pick: (w: WorkData) => T) =>
      multiSelectedWork.every((w) => pick(w) === pick(first));

    setYear(allSame((w) => w.prodYear) ? first.prodYear : null);
    setMaterial(allSame((w) => w.material) ? first.material : null);
    setMaterialEn(allSame((w) => w.materialEn) ? first.materialEn : null);
    setWorkType(allSame((w) => w.workType) ? first.workType : null);

    const commonSizes = Object.values(SizeUnit)
      .map((unit) => first.sizes.find((size) => size.unit === unit))
      .filter((size): size is SizeData =>
        Boolean(size && multiSelectedWork.every((w) =>
          isSameSizeData(size, w.sizes.find((item) => item.unit === size.unit))
        ))
      );
    const mainSizeUnitMatch = allSame((w) => w.mainSizeUnit);
    const dimensionsMatch = allSame((w) => w.dimensionsType);
    setSizes(commonSizes);
    setSizeUpdates([]);
    setMainSizeUnit(mainSizeUnitMatch ? first.mainSizeUnit : null);
    setSizeText(allSame((w) => w.sizeText) ? first.sizeText : null);
    setDimensionsType(dimensionsMatch ? first.dimensionsType : null);
    setDuration(allSame((w) => w.duration) ? first.duration : null);
    setIsPublic(allSame((w) => w.isPublic) ? first.isPublic : null);

    const countByTagId = new Map<number, number>();
    const n = multiSelectedWork.length;
    for (const work of multiSelectedWork) {
      const workTagIds = new Set((work.tags ?? []).map((t) => t.id));
      for (const id of workTagIds) {
        countByTagId.set(id, (countByTagId.get(id) ?? 0) + 1);
      }
    }
    const commonTagIds = [...countByTagId.entries()]
      .filter(([, count]) => count === n)
      .map(([id]) => id);
    setSelectedTagIds(commonTagIds);
    setMultiHasChanged(false);
  }, [multiSelectedWork]);

  const handleTagToggle = (tagId: number) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
    setMultiHasChanged(true);
  };

  const handleChangeWithValidate = (
    field: 'prodYear' | 'material' | 'materialEn' | 'sizeText' | 'duration',
    value: string
  ) => {
    if (!checkType(field, value)) return;
    if (field === 'prodYear') setYear(value);
    if (field === 'material') setMaterial(value);
    if (field === 'materialEn') setMaterialEn(value);
    if (field === 'sizeText') setSizeText(value);
    if (field === 'duration') setDuration(value);
    setMultiHasChanged(true);
  };

  const sizeUnits = Object.values(SizeUnit);
  const isDimensionsFixed = dimensionsType === DimensionsType.FIXED;
  const isDimensionsCustom = dimensionsType === DimensionsType.CUSTOM;

  const handleSizeChange = (unit: SizeUnit, next: SizeData) => {
    const nextSize = { ...next, unit };
    setSizeUpdates((prev) => {
      const otherUpdates = prev.filter((size) => size.unit !== unit);
      return [...otherUpdates, nextSize];
    });
    setMultiHasChanged(true);
  };

  const handleMainSizeUnitChange = (unit: SizeUnit) => {
    const hasSizeForUnit = sizeUpdates.some((size) => size.unit === unit)
      ? hasSizeDataForUnit(sizeUpdates, unit)
      : multiSelectedWork.every((work) => hasSizeDataForUnit(work.sizes, unit));

    if (!hasSizeForUnit) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SET_MAIN_SIZE_UNIT_REQUIRES_SIZE_FOR_ALL'),
      });
      return;
    }
    setMainSizeUnit(unit);
    setMultiHasChanged(true);
  };

  const handleDimensionsTypeChange = (next: DimensionsType) => {
    setDimensionsType(next);
    setMultiHasChanged(true);
  };

  const handleVisibleChange = async (field: WorkPropertyVisibleToggleField) => {
    if (!isEditMode || !workPropertyVisible) return;
    await updateWorkPropertyVisible({
      ...workPropertyVisible,
      [field]: !workPropertyVisible[field],
    });
  };

  const handleIsPublicToggle = (value: boolean) => {
    if (!isEditMode || !isOwner) return;
    setIsPublic(value);
    setMultiHasChanged(true);
  };

  if (!workPropertyVisible) return null;
  if (multiSelectedWork.length === 0) return null;

  const handleBulkDeleteSelectedWorks = () => {
    if (!isOwner || multiSelectedWork.length === 0) return;
    const ids = multiSelectedWork.map((w) => w.id);
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_DELETE_WORKS_BULK', { count: ids.length }),
      callBack: async () => {
        await deleteWorksBulk(ids);
      },
    });
  };

  const handleBulkUpdate = async () => {
    if (!isOwner || multiSelectedWork.length === 0) return;
    const hasInvalidMainSizeUnit = multiSelectedWork.some((work) => {
      const nextSizes = mergeWorkSizeUpdates(work.sizes, sizeUpdates);
      const nextMainSizeUnit = mainSizeUnit ?? work.mainSizeUnit;
      return !hasValidMainSizeUnitForUpdate(nextSizes, nextMainSizeUnit);
    });

    if (hasInvalidMainSizeUnit) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SET_MAIN_SIZE_UNIT_REQUIRES_SIZE_FOR_ALL'),
      });
      return;
    }

    const workList: UpdateWorkBulkInfoItemReq[] = multiSelectedWork.map((w) => {
      const item: UpdateWorkBulkInfoItemReq = { id: w.id };
      if (year !== null) item.prodYear = year;
      if (material !== null) item.material = material;
      if (materialEn !== null) item.materialEn = materialEn;
      if (workType !== null) {
        item.workType = workType;
        if (usesWorkDuration(workType) && duration !== null) {
          item.duration = duration;
        }
      }
      if (dimensionsType !== null) {
        item.dimensionsType = dimensionsType;
      }
      if (sizeText !== null) item.sizeText = sizeText;
      if (sizeUpdates.length > 0 || mainSizeUnit !== null) {
        const nextSizes = mergeWorkSizeUpdates(w.sizes, sizeUpdates);
        const normalizedSize = normalizeWorkSizesForUpdate(
          nextSizes,
          mainSizeUnit ?? w.mainSizeUnit,
        );
        if (sizeUpdates.length > 0) {
          item.sizes = normalizedSize.sizes;
        }
        item.mainSizeUnit = normalizedSize.mainSizeUnit;
      }
      item.tagIds = selectedTagIds;
      if (isPublic !== null) {
        item.isPublic = isPublic;
      }
      return item;
    });
    await updateWorkBulkInfo({ workList });
  };

  return (
    <MultiWorkInfoContainer>
      <MultiTitle>{multiSelectedWork.length} works selected</MultiTitle>
      <WorkInfo>
          <WorkInfoRow>
            <WorkInfoLabel>Year</WorkInfoLabel>
            <MoleculeInputDiv
              value={year || ''}
              defaultValue={'Year'}
              DefaultDiv={DefaultDiv}
              placeholder={'Year'}
              handleChange={(e) => handleChangeWithValidate('prodYear', e.target.value)}
              inputStyle={WorkViewerInfo}
              StyledDiv={Info}
              minLimit={INPUT_YEAR_MIN_LENGTH}
              maxLimit={INPUT_YEAR_MAX_LENGTH}
            />
          </WorkInfoRow>
          <WorkInfoMaterial>
            <WorkInfoLabel>Material {isWorkEng ? '(en)' : ''}</WorkInfoLabel>
            <MoleculeTextAreaDiv
              value={(isWorkEng ? materialEn : material) ?? ''}
              defaultValue={'Material'}
              DefaultDiv={DefaultDiv}
              placeholder={'Material'}
              handleChange={(e) => handleChangeWithValidate(
                isWorkEng ? 'materialEn' : 'material',
                e.target.value,
              )}
              textareaStyle={WorkViewerInfoTextArea}
              StyledDiv={Info}
            />
          </WorkInfoMaterial>
          {(!workType || !usesWorkDuration(workType)) && (
            <WorkInfoSizes>
              <WorkInfoLabel>Size</WorkInfoLabel>
              <SizeContainer>
                <DimensionsTypeSelect
                  value={dimensionsType}
                  onChange={handleDimensionsTypeChange}
                />
                <SizeInputContainer $dimensionsType={dimensionsType}>
                  {isDimensionsFixed && sizeUnits.map((unit) => {
                    const size = sizeUpdates.find((item) => item.unit === unit)
                      ?? sizes?.find((item) => item.unit === unit)
                      ?? { width: '', height: '', unit };
                    return (
                      <SizeInputWithMainUnit
                        key={unit}
                        value={size}
                        onChange={(next) => handleSizeChange(unit, next)}
                        StyledDiv={Info}
                        isMainUnit={mainSizeUnit === unit}
                        onMainUnitChange={() => handleMainSizeUnitChange(unit)}
                      />
                    );
                  })}
                  {isDimensionsCustom && (
                    <MoleculeInputDiv
                      value={sizeText ?? ''}
                      defaultValue={'Custom size'}
                      DefaultDiv={DefaultDiv}
                      placeholder={'Custom size'}
                      handleChange={(e) => handleChangeWithValidate('sizeText', e.target.value)}
                      inputStyle={WorkViewerInfo}
                      StyledDiv={Info}
                      minLimit={INPUT_SIZE_TEXT_MIN_LENGTH}
                      maxLimit={INPUT_SIZE_TEXT_MAX_LENGTH}
                    />
                  )}
                </SizeInputContainer>
              </SizeContainer>
            </WorkInfoSizes>
          )}
          {!!workType && usesWorkDuration(workType) && (
            <WorkInfoRow>
              <WorkInfoLabel>Duration</WorkInfoLabel>
              <MoleculeInputDiv
                value={duration || ''}
                defaultValue={'00:00:00'}
                DefaultDiv={DefaultDiv}
                placeholder={'00:00:00 (hh:mm:ss)'}
                handleChange={(e) => handleChangeWithValidate('duration', e.target.value)}
                inputStyle={WorkViewerInfo}
                StyledDiv={Info}
                minLimit={INPUT_RUNNING_TIME_MIN_LENGTH}
                maxLimit={INPUT_RUNNING_TIME_MAX_LENGTH}
              />
            </WorkInfoRow>
          )}
          {(workPropertyVisible.workType || isEditMode) && (
            <WorkInfoRow>
              <WorkInfoLabel>Type</WorkInfoLabel>
              <OrgInputDivVisiWrapper>
                <OrgSelectDivVisi
                  value={workType || WorkType.NONE}
                  selectType={SelectType.WORK_TYPE}
                  handleChange={(value) => {
                    setWorkType(value as WorkType);
                    setMultiHasChanged(true);
                  }}
                  StyledDiv={Info}
                  visible={workPropertyVisible.workType}
                  changeVisible={() => handleVisibleChange('workType')}
                />
              </OrgInputDivVisiWrapper>
            </WorkInfoRow>
          )}
          {isEditMode && (
          <WorkInfoTags>
              <WorkInfoLabel>Label</WorkInfoLabel>
              <TagContainer>
                {visibleTags.map((tag) => (
                  <TagItem
                    key={tag.id}
                    $isActive={selectedTagIds.includes(tag.id)}
                    onClick={() => handleTagToggle(tag.id)}
                  >
                    {tag.name}
                  </TagItem>
                ))}
              </TagContainer>
          </WorkInfoTags>
          )}
        </WorkInfo>
        {isEditMode && isOwner && (
          <WorkAccessToggles>
            <MoleculeDivToggle
              value={isPublic === true}
              name="Public"
              handleToggle={(e) => handleIsPublicToggle(e.target.checked)}
            />
          </WorkAccessToggles>
        )}
        {isEditMode && isOwner && (
          <MultiEditActionsRow>
            {multiHasChanged && (
              <HintWrapper
                content={getHintMessage('UPDATE_SELECTED_WORKS')}
                toLeft={true}
              >
                <UniversalIconBtn
                  isPrimary={true}
                  size="fit"
                  border={true}
                  backgroundImg={confirmIcon}
                  backgroundImgHover={confirmIcon2}
                  onClick={handleBulkUpdate}
                />
              </HintWrapper>
            )}
            <HintWrapper 
              content={getHintMessage('DELETE_SELECTED_WORKS')} 
              toLeft={true}>
              <UniversalIconBtn
                size="fit"
                border={true}
                backgroundImg={deleteCloseIcon}
                backgroundImgHover={deleteOpenIcon}
                onClick={handleBulkDeleteSelectedWorks}
              />
            </HintWrapper>
          </MultiEditActionsRow>
        )}
      </MultiWorkInfoContainer>
  );
};

const MultiEditActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5vh;
  margin-top: 8px;
`;

const MultiWorkInfoContainer = styled.div`
  width: 70%;
  align-self: flex-end;
  display: flex;
  flex-direction: column;

  padding: 4px 0 8px 0;

  gap: 2px;
`;

const MultiTitle = styled.h2`
  width: 100%;
  min-height: 28px;

  display: flex;
  align-items: center;

  margin-bottom: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const WorkInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 2px;
  gap: 6px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const WorkInfoRow = styled.div`
  height: 18px;
  min-height: 18px;
  margin-bottom: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const WorkInfoSizes = styled.div`
  height: fit-content;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const WorkInfoMaterial = WorkInfoSizes;

const SizeContainer = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const SizeInputContainer = styled.div<{ $dimensionsType: DimensionsType | null }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: ${({ $dimensionsType }) => {
    if ($dimensionsType === DimensionsType.FIXED) return '90px';
    if ($dimensionsType === DimensionsType.CUSTOM) return '24px';
    return '0px';
  }};
  margin-top: 4px;
  overflow: hidden;
  transition: height 0.5s ease-in-out;
`;

const WorkInfoLabel = styled.span`
  flex-shrink: 0;
  min-width: 70px;
  padding-top: 2px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;

const WorkAccessToggles = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0 4px;
`;

const OrgInputDivVisiWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`;

const Info = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.2;
`;

const DefaultDiv = styled.div`
  width: fit-content;
  color: ${({ theme }) => theme.colors.color_Gray_05} !important;
  ${({ theme }) => theme.typography.Body_04};

  background-color: ${({ theme }) => theme.colors.color_Gray_06};
`;

const WorkInfoTags = styled.div`
  height: fit-content;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const TagContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TagItem = styled.div<{ $isActive: boolean }>`
  cursor: pointer;
  width: fit-content;
  border-bottom: 1px dotted
    ${({ $isActive, theme }) => ($isActive ? theme.colors.color_Gray_03 : 'transparent')};
  color: ${({ $isActive, theme }) => ($isActive ? theme.colors.color_Gray_03 : theme.colors.color_Gray_04)};
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0.5)};
  &:hover {
    transform: scale(0.95);
  }
  transition: all 0.3s ease-in-out;
`;

export default MultiWorkViewer;
